import { useState } from "react";
import { ArrowLeft, ArrowRight, Eye, EyeOff, Loader2, MailCheck } from "lucide-react";
import { cn } from "@/lib/utils";

// Shared bits for email sign-in and the reset page.
export const authErr = (e) => {
  const d = e?.response?.data?.detail;
  const s = e?.response?.status;
  if (s === 404 || s === 405) return "Email sign-in is being switched on. Please use Google for now, or try again shortly.";
  if (s === 429) return "Too many attempts. Please wait 15 minutes and try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ") || "Please check your details.";
  if (d?.message) return d.message;
  if (s === 401) return "That email and password don't match.";
  return e?.request && !e?.response ? "Can't reach the server. Check your connection." : "Something went wrong. Please try again.";
};

export const strength = (p) => {
  let n = 0;
  if (p.length >= 8) n++;
  if (p.length >= 12) n++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) n++;
  if (/\d/.test(p) && /[^A-Za-z0-9]/.test(p)) n++;
  return p ? Math.max(1, n) : 0;
};
const LABEL = ["", "Weak", "Fair", "Good", "Strong"];
const BAR = ["", "bg-red-500", "bg-amber-500", "bg-[#D4AF37]", "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]"];

export const Strength = ({ p }) => {
  const n = strength(p);
  if (!p) return null;
  return (
    <div className="mt-2" data-testid="auth-strength">
      <div className="flex gap-1">{[1, 2, 3, 4].map((i) => <span key={i} className={cn("h-1 flex-1 rounded-full transition-colors duration-300", i <= n ? BAR[n] : "bg-zinc-800")} />)}</div>
      <p className="mt-1 flex justify-between text-[11px] text-zinc-500"><span data-testid="auth-strength-label">{LABEL[n]}</span><span>8+ characters, mix letters, numbers & symbols</span></p>
    </div>
  );
};

const inputCls = "h-12 w-full rounded-xl border border-zinc-800 bg-[#09090B]/90 px-4 text-base text-zinc-100 placeholder:text-zinc-600 transition-[border-color,box-shadow] duration-200 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/25 sm:text-sm";

export const Field = ({ label, type = "text", value, onChange, testid, autoComplete, autoFocus, right }) => {
  const [show, setShow] = useState(false);
  const pw = type === "password";
  return (
    <div>
      <span className="mb-1.5 flex items-center justify-between text-[11px] font-medium uppercase tracking-wider text-zinc-400"><label htmlFor={testid}>{label}</label>{right}</span>
      <span className="relative block">
        <input id={testid} type={pw && show ? "text" : type} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} autoFocus={autoFocus} className={cn(inputCls, pw && "pr-11")} data-testid={testid} />
        {pw && <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-500 transition-colors hover:text-[#F3C649]" data-testid={`${testid}-toggle`}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
      </span>
    </div>
  );
};

export const GoldButton = ({ busy, children, testid, disabled }) => (
  <button type="submit" disabled={busy || disabled} className="group flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#D4AF37] via-[#F3C649] to-[#C9A230] text-sm font-semibold text-[#09090B] shadow-[0_4px_20px_rgba(212,175,55,0.25)] transition-[transform,box-shadow,filter] duration-200 hover:shadow-[0_6px_28px_rgba(212,175,55,0.45)] hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60" data-testid={testid}>
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{children}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>}
  </button>
);

export const ErrorNote = ({ msg }) => msg ? <p role="alert" className="auth-shake rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid="auth-error">{msg}</p> : null;

export const Sent = ({ email, onBack }) => (
  <div className="auth-in space-y-4 text-center" data-testid="auth-forgot-sent">
    <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"><MailCheck className="h-6 w-6" /></span>
    <div><p className="font-display text-lg text-zinc-50">Check your inbox</p><p className="mt-1 text-sm text-zinc-400">If an account exists for <span className="text-zinc-200">{email}</span>, a reset link is on its way. It works for 1 hour.</p></div>
    <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 transition-colors hover:text-[#F3C649]" data-testid="auth-forgot-back-signin"><ArrowLeft className="h-3.5 w-3.5" />Back to sign in</button>
  </div>
);
